import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Download, FileSpreadsheet, Loader2, ScrollText, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { useDirectory } from "@/hooks/useMeta";
import { fmtDateTime } from "@/lib/format";
import { Btn, EmptyState, LabeledSelect, PageHeader, Pagination } from "@/components/dashboard/Ui";

const ACTION_COLORS = {
  appointment: "#2f5d83",
  submission: "#087f8c",
  user: "#3e9b54",
  email: "#b08a2a",
  intake_link: "#4b6a7a",
};

export default function AuditPage() {
  const [filters, setFilters] = useState({ action: "", actor_id: "", entity_type: "", date_from: "", date_to: "", q: "", page: 1 });
  const [exporting, setExporting] = useState(null);
  const { data: directory } = useDirectory();
  const { data: actions } = useQuery({ queryKey: ["audit-actions"], queryFn: async () => (await api.get("/audit/actions")).data.actions, staleTime: Infinity });

  const params = { ...filters, action: filters.action || undefined, actor_id: filters.actor_id || undefined, entity_type: filters.entity_type || undefined, date_from: filters.date_from || undefined, date_to: filters.date_to || undefined, q: filters.q || undefined };
  const { data, isLoading } = useQuery({ queryKey: ["audit", params], queryFn: async () => (await api.get("/audit", { params: { ...params, page_size: 50 } })).data, placeholderData: keepPreviousData });

  const set = (patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page || 1 }));
  const hasFilters = filters.action || filters.actor_id || filters.entity_type || filters.date_from || filters.date_to || filters.q;

  const onExport = async (fmt) => {
    setExporting(fmt);
    try {
      const { page, ...rest } = params;
      await downloadFile("/audit/export", { ...rest, fmt }, `emerge-audit.${fmt}`);
    } catch (e) {
      toast.error(errorMessage(e, "Export failed"));
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="audit-page">
      <PageHeader
        title="Audit log"
        description="Who changed what, and when. Read-only."
        actions={
          <>
            <Btn variant="secondary" onClick={() => onExport("csv")} disabled={!!exporting} data-testid="audit-export-csv-button">
              {exporting === "csv" ? <Loader2 className="animate-spin" /> : <Download />} CSV
            </Btn>
            <Btn variant="secondary" onClick={() => onExport("xlsx")} disabled={!!exporting} data-testid="audit-export-xlsx-button">
              {exporting === "xlsx" ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />} Excel
            </Btn>
          </>
        }
      />

      <div className="emerge-card mb-4 grid gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="relative xl:col-span-2">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="emerge-input !h-10 ps-9 text-sm" placeholder="Search summary, actor, record..." value={filters.q} onChange={(e) => set({ q: e.target.value })} data-testid="audit-search-input" />
        </div>
        <LabeledSelect id="a-action" value={filters.action} onChange={(e) => set({ action: e.target.value })} data-testid="audit-filter-action-select" aria-label="Filter by action">
          <option value="">All actions</option>
          {(actions || []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </LabeledSelect>
        <LabeledSelect id="a-actor" value={filters.actor_id} onChange={(e) => set({ actor_id: e.target.value })} data-testid="audit-filter-actor-select" aria-label="Filter by actor">
          <option value="">All actors</option>
          {(directory || []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </LabeledSelect>
        <input type="date" className="emerge-input !h-10 text-sm" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} aria-label="From date" data-testid="audit-filter-from-input" />
        <input type="date" className="emerge-input !h-10 text-sm" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} aria-label="To date" data-testid="audit-filter-to-input" />
        {hasFilters && (
          <div className="md:col-span-3 xl:col-span-6 flex justify-end">
            <Btn variant="ghost" size="sm" onClick={() => setFilters({ action: "", actor_id: "", entity_type: "", date_from: "", date_to: "", q: "", page: 1 })}>
              <X /> Clear filters
            </Btn>
          </div>
        )}
      </div>

      <div className="emerge-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (data?.items || []).length === 0 ? (
          <EmptyState icon={ScrollText} title="No audit entries" description="Actions like assigning appointments or changing statuses will be recorded here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm" data-testid="audit-table">
              <thead className="bg-secondary/70 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">When</th>
                  <th className="px-4 py-3 text-start font-semibold">Actor</th>
                  <th className="px-4 py-3 text-start font-semibold">Action</th>
                  <th className="px-4 py-3 text-start font-semibold">Summary</th>
                  <th className="px-4 py-3 text-start font-semibold">Record</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-t border-border table-row-hover align-top" data-testid="audit-row">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(a.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{a.actor_name}</p>
                      <p className="text-xs capitalize text-muted-foreground">{a.actor_role}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-2 py-0.5 font-mono text-xs">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ACTION_COLORS[a.entity_type] || "#63757a" }} />
                        {a.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {a.summary}
                      {a.details?.changes && (
                        <ul className="mt-1 text-xs text-muted-foreground">
                          {Object.entries(a.details.changes).map(([k, v]) => (
                            <li key={k}>
                              {k}: {String(v.from ?? "-")} → {String(v.to ?? "-")}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.entity_type === "submission" && a.entity_id ? (
                        <a href={`/dashboard/submissions/${a.entity_id}`} className="text-xs font-semibold text-primary hover:underline">
                          Open submission
                        </a>
                      ) : a.entity_type === "appointment" && a.entity_id ? (
                        <a href={`/dashboard/schedule?appointment=${a.entity_id}`} className="text-xs font-semibold text-primary hover:underline">
                          Open appointment
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">{a.entity_type}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={filters.page} pageSize={50} total={data.total} onChange={(p) => set({ page: p })} />}
      </div>
    </div>
  );
}
